import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { JsonLd, SeoProps } from '@/types'

import { absoluteUrl } from '@/lib/business'
import { siteConfig } from '@/constants/site'

const serializeJsonLd = (data: JsonLd | JsonLd[]): string => {
    return JSON.stringify(data).replace(/</g, '\\u003c')
}

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    ogImageAlt = siteConfig.ogImageAlt,
    path = '/',
    keywords = siteConfig.keywords,
    jsonLd,
    noIndex = false
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const canonical = absoluteUrl(path)
    const image = absoluteUrl(ogImage)

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta name='keywords' content={keywords.join(', ')} />
            <meta name='robots' content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />
            <link rel='canonical' href={canonical} />

            <meta name='geo.region' content='IN-DL' />
            <meta name='geo.placename' content='New Gupta Colony, Delhi' />

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={siteConfig.locale} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={canonical} />
            <meta property='og:image' content={image} />
            <meta property='og:image:alt' content={ogImageAlt} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image} />

            {jsonLd && (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
                />
            )}
        </Head>
    )
}
